import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import { Box, Stack, Typography } from "@mui/material";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { DateTime } from "luxon";
import { type FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FacetFilterRail } from "../../../components/UIToolkit/Filters/FacetFilterRail";
import { FilteredEmptyState } from "../../../components/UIToolkit/Filters/FilteredEmptyState";
import { FilterRailFrame } from "../../../components/UIToolkit/Filters/FilterRailFrame";
import type { FacetSpec } from "../../../components/UIToolkit/Filters/facetSections";
import { SystemValidationTextField } from "../../../components/UIToolkit/Inputs/SystemValidationTextField";
import { EmptyState } from "../../../components/UIToolkit/molecules/EmptyState";
import { SettingsSection } from "../../../components/UIToolkit/molecules/SettingsSection";
import SystemDialog from "../../../components/UIToolkit/Popups/Dialog";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { SystemDataGrid } from "../../../components/UIToolkit/Tables/SystemDataGrid";
import { useFilterRailHandoff, useGridFacetFilters } from "../../../hooks/useFacetFilters";
import type { PbjExcludedEmployeeRow } from "../../../utils/models";
import { usePbjExcludedEmployees, usePbjExcludeEmployee } from "../hooks/usePbjAudit";
import type { WithBuilding } from "../network/buildingQueries";
import { usePbjNetwork } from "../network/PbjNetworkContext";
import { useBuildingGridParts, useWithBuildingFacet } from "../network/useBuildingGridParts";
import { dataTableSx, sectionCardSx } from "../PredictionTab/colors";
import { useFacetLabels } from "../pbjFacetLabels";
import { textFacet } from "../pbjFacets";
import { sectionFrameHeight } from "../pbjGridCells";
import { pbjSortOptions } from "../pbjGridSorting";
import { ExcludeEmployeeDialog } from "./ExcludeEmployeeDialog";
import { ImportExclusionsDialog } from "./ImportExclusionsDialog";

type Props = {
	startDate: string;
	endDate: string;
};

type ExcludedGridRow = WithBuilding<PbjExcludedEmployeeRow>;

/** One row per excluded employee per building (facility-group scoped); pbj id keys the exclusion. */
const rowId = (row: ExcludedGridRow) => `${row.buildingId}|${row.pbjEmployeeId}`;

// "Excluded At" renders in the DEPARTMENT's timezone (not the viewer's), consistent with the other
// PBJ audit timestamps (see ShiftDurationReviewSection.formatReviewDateTime). The value is a tz-aware
// timestamptz, so the instant is correct either way — this just controls which wall-clock is shown.
const formatDateTime = (iso: string | null, zone: string): string => {
	if (!iso) return "—";
	// DEFAULT: an empty zone (the ChosenDepartment placeholder before the dept loads) falls back to the
	// viewer's zone rather than dropping the timestamp — the audit query is gated on a loaded dept anyway.
	const dt = zone ? DateTime.fromISO(iso).setZone(zone) : DateTime.fromISO(iso);
	return dt.isValid ? dt.toLocaleString(DateTime.DATETIME_MED) : "—";
};

/**
 * PBJ Audit → "Excluded from PBJ": the roster of employees flagged out of the CMS filing. Each row shows
 * who/why/when and the hours dropped for the viewed quarter. Inline actions edit the (mandatory, non-blank)
 * reason or un-exclude the employee; a toolbar "Bulk import" opens the Name + Reason upload/preview flow.
 * Reads live via `usePbjExcludedEmployees`; edits go through `usePbjExcludeEmployee` (invalidates the audit
 * family, so the row updates/disappears without a manual refresh).
 */
const COLUMN_FACET: Readonly<Record<string, string>> = {
	reason: "reason",
	excludedBy: "excluded_by",
	departmentId: "department",
};

export const ExcludedFromPbjTable: FC<Props> = ({ startDate, endDate }) => {
	const { t } = useTranslation();
	// Exclusions are written to the header department, the only building editable from here.
	const { homeDepartmentId: departmentId, buildingTimezone } = usePbjNetwork();
	const { data, isLoading, isError } = usePbjExcludedEmployees(startDate, endDate);
	const excludeMutation = usePbjExcludeEmployee();
	const building = useBuildingGridParts<ExcludedGridRow>();

	const [importOpen, setImportOpen] = useState(false);
	const [excludeOpen, setExcludeOpen] = useState(false);
	// Edit-reason dialog: the target row + the in-progress reason draft.
	const [editRow, setEditRow] = useState<ExcludedGridRow | null>(null);
	const [reasonDraft, setReasonDraft] = useState("");
	const [reasonSubmitted, setReasonSubmitted] = useState(false);
	// Un-exclude confirm dialog target.
	const [unexcludeRow, setUnexcludeRow] = useState<ExcludedGridRow | null>(null);

	const openEdit = useCallback((row: ExcludedGridRow) => {
		setEditRow(row);
		setReasonDraft(row.reason ?? "");
		setReasonSubmitted(false);
	}, []);

	const closeEdit = useCallback(() => {
		if (excludeMutation.isPending) return;
		setEditRow(null);
		setReasonDraft("");
		setReasonSubmitted(false);
	}, [excludeMutation.isPending]);

	const trimmedReason = reasonDraft.trim();
	const reasonValid = trimmedReason.length > 0;

	// Edit-reason confirm: re-assert the exclusion with the new (non-blank) reason. Enforce non-blank
	// client-side before calling — the button is disabled when blank, and this is the final guard.
	const handleSaveReason = useCallback(() => {
		setReasonSubmitted(true);
		if (!editRow || !reasonValid || excludeMutation.isPending) return;
		excludeMutation.mutate({ departmentId, pbjEmployeeId: editRow.pbjEmployeeId, exclude: true, reason: trimmedReason }, { onSuccess: () => closeEdit() });
	}, [editRow, reasonValid, excludeMutation, departmentId, trimmedReason, closeEdit]);

	// Un-exclude: clear the flag (reason nulled server-side).
	const handleUnexclude = useCallback(() => {
		if (!unexcludeRow || excludeMutation.isPending) return;
		excludeMutation.mutate(
			{ departmentId, pbjEmployeeId: unexcludeRow.pbjEmployeeId, exclude: false, reason: null },
			{ onSuccess: () => setUnexcludeRow(null) },
		);
	}, [unexcludeRow, excludeMutation, departmentId]);

	const columns: GridColDef<ExcludedGridRow>[] = useMemo(
		() => [
			...building.columns,
			{ field: "name", headerName: t("pbj_model.audit.excluded.cols.name"), minWidth: 160, flex: 1 },
			{ field: "pbjEmployeeId", headerName: t("pbj_model.audit.excluded.cols.pbj_id"), minWidth: 120 },
			{ field: "departmentId", headerName: t("pbj_model.audit.excluded.cols.department"), minWidth: 140, flex: 1 },
			{ field: "reason", headerName: t("pbj_model.audit.excluded.cols.reason"), minWidth: 200, flex: 1.5, valueGetter: (v: string | null) => v ?? "—" },
			{ field: "excludedBy", headerName: t("pbj_model.audit.excluded.cols.excluded_by"), minWidth: 160, flex: 1, valueGetter: (v: string | null) => v ?? "—" },
			{
				field: "excludedAt",
				headerName: t("pbj_model.audit.excluded.cols.excluded_at"),
				minWidth: 170,
				valueGetter: (v: string | null, row: ExcludedGridRow) => formatDateTime(v, buildingTimezone(row.buildingId)),
			},
			{
				field: "excludedHours",
				headerName: t("pbj_model.audit.excluded.cols.excluded_hours"),
				minWidth: 130,
				type: "number",
				align: "left",
				headerAlign: "left",
				valueFormatter: (v: number) => (v == null ? "" : v.toFixed(2)),
			},
			{
				field: "actions",
				headerName: t("pbj_model.audit.excluded.cols.actions"),
				minWidth: 110,
				sortable: false,
				filterable: false,
				disableExport: true,
				renderCell: (params: GridRenderCellParams<ExcludedGridRow>) => {
					const editable = building.canEditRow(params.row);
					// Another building's exclusion is changed from that building's page.
					const readOnlyHint = t("pbj_model.network.edit_in_building", { building: params.row.buildingName });
					return (
						<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, height: "100%" }}>
							<SystemButton
								buttonVariant="borderless"
								size="small"
								onClick={() => openEdit(params.row)}
								disabled={!editable}
								leadingIcon={<EditOutlinedIcon sx={{ fontSize: 18 }} />}
								tooltip={editable ? t("pbj_model.audit.excluded.edit_reason") : readOnlyHint}
								aria-label={t("pbj_model.audit.excluded.edit_reason")}
								noFullWidthMobile
								data-testid={`pbj-excluded-edit-${params.row.buildingId}-${params.row.pbjEmployeeId}`}
							/>
							<SystemButton
								buttonVariant="borderless"
								size="small"
								onClick={() => setUnexcludeRow(params.row)}
								disabled={!editable}
								leadingIcon={<PersonRemoveOutlinedIcon sx={{ fontSize: 18 }} />}
								tooltip={editable ? t("pbj_model.audit.excluded.unexclude") : readOnlyHint}
								aria-label={t("pbj_model.audit.excluded.unexclude")}
								noFullWidthMobile
								data-testid={`pbj-excluded-unexclude-${params.row.buildingId}-${params.row.pbjEmployeeId}`}
							/>
						</Box>
					);
				},
			},
		],
		[t, openEdit, buildingTimezone, building.columns, building.canEditRow],
	);

	// pbj ids already excluded — passed to the exclude dialog's picker so an employee can't be double-excluded.
	const excludedPbjIds = useMemo(() => new Set((data ?? []).filter((r) => r.buildingId === departmentId).map((r) => r.pbjEmployeeId)), [data, departmentId]);

	const excludeEmployeeButton = (
		<SystemButton
			buttonVariant="secondary"
			size="small"
			onClick={() => setExcludeOpen(true)}
			leadingIcon={<PersonAddAltOutlinedIcon sx={{ fontSize: 18 }} />}
			noFullWidthMobile
			data-testid="pbj-excluded-exclude-employee-button"
		>
			{t("pbj_model.audit.excluded.exclude_employee")}
		</SystemButton>
	);

	const bulkImportButton = (
		<SystemButton
			buttonVariant="secondary"
			size="small"
			onClick={() => setImportOpen(true)}
			leadingIcon={<UploadFileOutlinedIcon sx={{ fontSize: 18 }} />}
			noFullWidthMobile
			data-testid="pbj-excluded-bulk-import-button"
		>
			{t("pbj_model.audit.excluded.bulk_import")}
		</SystemButton>
	);

	const rows = useMemo(() => data ?? [], [data]);
	const labels = useFacetLabels();
	const facets = useMemo<FacetSpec<ExcludedGridRow>[]>(
		() => [
			...building.facets,
			{
				dimension: textFacet<ExcludedGridRow>("reason", (r) => r.reason),
				title: t("pbj_model.audit.excluded.cols.reason"),
				getLabel: labels.raw,
				searchable: true,
			},
			{
				dimension: textFacet<ExcludedGridRow>("excluded_by", (r) => r.excludedBy),
				title: t("pbj_model.audit.excluded.cols.excluded_by"),
				getLabel: labels.raw,
			},
			{
				dimension: textFacet<ExcludedGridRow>("department", (r) => r.departmentId),
				title: t("pbj_model.audit.excluded.cols.department"),
				getLabel: labels.raw,
			},
		],
		[t, labels, building.facets],
	);
	const filters = useGridFacetFilters(rows, facets, building.sharedGroups);
	const railHandoff = useFilterRailHandoff(filters.highlight, useWithBuildingFacet(COLUMN_FACET), t("pbj_model.filter.in_rail"));

	const sortingOptions = useMemo(
		() =>
			pbjSortOptions<ExcludedGridRow>([
				...building.sortFields,
				{ field: "name", label: t("pbj_model.audit.excluded.cols.name") },
				{ field: "pbjEmployeeId", label: t("pbj_model.audit.excluded.cols.pbj_id") },
				{ field: "departmentId", label: t("pbj_model.audit.excluded.cols.department") },
				{ field: "reason", label: t("pbj_model.audit.excluded.cols.reason") },
				{ field: "excludedBy", label: t("pbj_model.audit.excluded.cols.excluded_by") },
				{ field: "excludedAt", label: t("pbj_model.audit.excluded.cols.excluded_at") },
				{ field: "excludedHours", label: t("pbj_model.audit.excluded.cols.excluded_hours") },
			]),
		[t, building.sortFields],
	);

	const toolbarButtons = (
		<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
			{excludeEmployeeButton}
			{bulkImportButton}
		</Box>
	);

	return (
		<>
			<SettingsSection
				layout="stack"
				title={t("pbj_model.audit.excluded.section.title")}
				description={t("pbj_model.audit.excluded.section.subtitle")}
				dataTestId="pbj-excluded-section"
				sx={(theme) => sectionCardSx(theme)}
			>
				<FilterRailFrame
					flat
					height={sectionFrameHeight(rows.length)}
					dataTestId="pbj-excluded-filters-frame"
					rail={
						<FacetFilterRail
							rows={rows}
							facets={facets}
							filters={filters}
							collapsed={railHandoff.railCollapsed}
							onCollapsedChange={railHandoff.setRailCollapsed}
							dataTestId="pbj-excluded-filters"
						/>
					}
				>
					<SystemDataGrid<ExcludedGridRow>
						rows={filters.result.rows}
						columns={columns}
						getRowId={rowId}
						canFilterByColumn={railHandoff.canFilterByColumn}
						onFilterByColumn={railHandoff.onFilterByColumn}
						filterByHint={railHandoff.filterByHint}
						sortingOptions={sortingOptions}
						showSortSummary
						exportOptions={{ fileName: t("pbj_model.audit.excluded.section.title") }}
						searchableColumns={["name", "pbjEmployeeId", "reason", "excludedBy"]}
						searchPlaceholder={t("pbj_model.audit.excluded.search")}
						loading={isLoading}
						error={isError}
						errorMessage={t("pbj_model.audit.excluded.failed")}
						defaultPageSize={25}
						leadingToolbarNodes={toolbarButtons}
						emptyState={
							<FilteredEmptyState
								filtered={filters.activeCount > 0}
								onClearFilters={filters.clear}
								unfilteredState={
									<EmptyState
										title={t("pbj_model.audit.excluded.empty_title")}
										hint={t("pbj_model.audit.excluded.empty_hint")}
										action={toolbarButtons}
										dataTestId="pbj-excluded-empty"
									/>
								}
								dataTestId="pbj-excluded-filtered-empty"
							/>
						}
						sx={(theme) => dataTableSx(theme)}
						dataTestId="pbj-model-excluded-table"
					/>
				</FilterRailFrame>
			</SettingsSection>

			<ExcludeEmployeeDialog
				open={excludeOpen}
				onClose={() => setExcludeOpen(false)}
				departmentId={departmentId}
				startDate={startDate}
				endDate={endDate}
				excludedPbjIds={excludedPbjIds}
			/>

			<ImportExclusionsDialog open={importOpen} onClose={() => setImportOpen(false)} departmentId={departmentId} />

			<SystemDialog
				open={editRow !== null}
				onClose={closeEdit}
				title={t("pbj_model.audit.excluded.edit_dialog.title", { name: editRow?.name ?? "" })}
				body={
					<Stack spacing={1} sx={{ pt: 1, minWidth: { xs: "auto", sm: 380 } }}>
						<Typography variant="body2" color="text.secondary">
							{t("pbj_model.audit.excluded.edit_dialog.description")}
						</Typography>
						<SystemValidationTextField
							value={reasonDraft}
							onChange={(e) => setReasonDraft(e.target.value)}
							label={t("pbj_model.audit.excluded.edit_dialog.reason_label")}
							placeholder={t("pbj_model.audit.excluded.edit_dialog.reason_placeholder")}
							required
							fullWidth
							submitTriggerred={reasonSubmitted}
							validation={{
								performValidation: "onlyAfterInitialChange",
								showValidWhenValid: "never",
								validationCallback: (v) => v.trim().length > 0,
								helperText: { invalid: t("pbj_model.audit.employees.exclusion.reason_required") },
							}}
							dataTestId="pbj-excluded-edit-reason-input"
						/>
					</Stack>
				}
				confirm={t("save")}
				confirmButtonVariant="primary"
				confirmDisabled={!reasonValid}
				confirmLoading={excludeMutation.isPending}
				onConfirm={handleSaveReason}
				cancel={t("cancel")}
				dataTestId="pbj-excluded-edit-dialog"
			/>

			<SystemDialog
				open={unexcludeRow !== null}
				onClose={() => (excludeMutation.isPending ? undefined : setUnexcludeRow(null))}
				title={t("pbj_model.audit.excluded.unexclude_dialog.title", { name: unexcludeRow?.name ?? "" })}
				body={t("pbj_model.audit.excluded.unexclude_dialog.body")}
				confirm={t("pbj_model.audit.excluded.unexclude")}
				confirmButtonVariant="primary"
				confirmLoading={excludeMutation.isPending}
				onConfirm={handleUnexclude}
				cancel={t("cancel")}
				dataTestId="pbj-excluded-unexclude-dialog"
			/>
		</>
	);
};
